import * as Ariakit from '@ariakit/react'
import { FC, ReactNode, useId } from 'react'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { getAvailabilityMatrix } from '../../../../../containers/EmbroideryContainer/combination-rules/engine'
import { PlacementId } from '../../../../../containers/EmbroideryContainer/combination-rules/placementIds'
import { EmbroideryTypeKey } from '../../../../../containers/EmbroideryContainer/combination-rules/types'
import { useEmbroideryAvailability } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryAvailabilityProvider'
import { useEmbroideryGarment } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryGarmentProvider'
import { useEmbroideryPrices } from '../../../../../containers/EmbroideryContainer/providers/EmbroideryPricingProvider'
import { useEmbroiderySelection } from '../../../../../containers/EmbroideryContainer/providers/EmbroiderySelectionProvider'
import { EmbroideryItem } from '../../../../../containers/EmbroideryContainer/providers/types'
import { Label } from '../../primitives/shared-form-styles'
import { ValidationMessage } from '../../primitives/ValidationMessage/ValidationMessage'

import { PlacementDisclosure } from './PlacementDisclosure'
import { placementStatusLabel } from './placementLabels'
import { PlacementOption } from './PlacementOption'
import { PlacementInstructions, PlacementList, PlacementPickerWrapper } from './styles'

type SharedProps = {
  typeKey: EmbroideryTypeKey
  /** The section heading, e.g. "TEXT POSITION". */
  label: string
  /** The line under it, e.g. "Select up to 3 text placements". */
  instructions?: string
  /**
   * A message about the group as a whole, e.g. "Add at least one text option to save". Every
   * pickable card is outlined as invalid while it is set.
   */
  errorMessage?: string
  /**
   * The item this picker is moving, if it is moving one rather than placing something new. Its own
   * placement, and everything else it is blocking by existing, then reads as open to it.
   */
  editingInstanceId?: string
  /**
   * The selection as the editor would save it, its unsaved work included, for an editor whose own
   * picks limit each other (text's three-placement cap, say). Defaults to the saved selection.
   */
  items?: readonly EmbroideryItem[]
  showPrice?: boolean
}

type SingleSelectProps = SharedProps & {
  mode: 'single'
  selectedPlacementId: PlacementId | undefined
  onSelectPlacement(placementId: PlacementId): void
}

type MultiSelectProps = SharedProps & {
  mode: 'multi'
  selectedPlacementIds: readonly PlacementId[]
  expandedPlacementId: PlacementId | undefined
  onTogglePlacement(placementId: PlacementId): void
  onToggleExpanded(placementId: PlacementId): void
  /** The form to show inside an open card. */
  renderPlacementForm(placementId: PlacementId): ReactNode
}

export type PlacementPickerProps = SingleSelectProps | MultiSelectProps

/**
 * Where a piece of embroidery can go on this garment, and which of those the customer has taken.
 */
export const PlacementPicker: FC<PlacementPickerProps> = ({ showPrice = false, ...props }) => {
  const { typeKey, label, instructions, errorMessage, editingInstanceId } = props
  const { matrix, matrixForEditing } = useEmbroideryAvailability()
  const { constraints } = useEmbroideryGarment()
  const { items: savedItems } = useEmbroiderySelection()
  const items = props.items ?? savedItems
  const { formatPrice, priceInCentsForPlacement } = useEmbroideryPrices()
  const {
    embroidery: { placement: copy },
  } = useTranslation()

  const labelId = `embroidery-placement-${typeKey}`
  const errorId = useId()
  const offered = matrix[typeKey].placements.filter(placement => placement.offered)

  if (offered.length === 0) return null

  // Exclude a placement's own existing item from its availability check,
  // so it doesn't read as blocked by itself.
  const availabilityForPlacement = (placementId: PlacementId) => {
    const ownItem = items.find(item => item.typeKey === typeKey && item.placementId === placementId)
    const setAsideId = editingInstanceId ?? ownItem?.instanceId

    // The provider's matrices are cached for the saved selection; an editor's own list is not.
    if (props.items !== undefined) {
      return getAvailabilityMatrix(
        items.filter(item => item.instanceId !== setAsideId),
        constraints
      )
    }
    return setAsideId === undefined ? matrix : matrixForEditing(setAsideId)
  }

  const cardFor = (placementId: PlacementId, available: boolean, statusLabel?: string) => {
    const priceLabel = showPrice
      ? formatPrice(priceInCentsForPlacement(typeKey, placementId), true)
      : undefined
    const disabled = !available

    if (props.mode === 'single') {
      return (
        <PlacementOption
          key={placementId}
          placementId={placementId}
          priceLabel={priceLabel}
          statusLabel={statusLabel}
          isSelected={props.selectedPlacementId === placementId}
          disabled={disabled}
          invalid={errorMessage !== undefined && !disabled}
          onSelect={props.onSelectPlacement}
        />
      )
    }

    const isSelected = props.selectedPlacementIds.includes(placementId)

    return (
      <PlacementDisclosure
        key={placementId}
        placementId={placementId}
        priceLabel={priceLabel}
        statusLabel={statusLabel}
        isSelected={isSelected}
        isExpanded={props.expandedPlacementId === placementId}
        disabled={disabled}
        invalid={errorMessage !== undefined && !disabled}
        copy={copy}
        onToggleSelected={props.onTogglePlacement}
        onToggleExpanded={props.onToggleExpanded}
        renderForm={() => props.renderPlacementForm(placementId)}
      />
    )
  }

  const cards = offered.map(({ placementId }) => {
    const placement = availabilityForPlacement(placementId)[typeKey].placements.find(
      p => p.placementId === placementId
    )
    const available = placement?.available ?? false
    const reason = placement?.reason
    const instanceIds = placement?.instanceIds

    return cardFor(
      placementId,
      available,
      placementStatusLabel({
        typeKey,
        placementId,
        reason: available ? undefined : reason,
        blockingInstanceIds: available ? undefined : instanceIds,
        items,
        copy,
      })
    )
  })

  const showInstructions = instructions !== undefined
  const describedBy = errorMessage === undefined ? undefined : errorId

  return (
    <PlacementPickerWrapper>
      <Label id={labelId} inset>
        {label}
      </Label>
      {showInstructions && <PlacementInstructions>{instructions}</PlacementInstructions>}

      {props.mode === 'single' ? (
        <Ariakit.RadioProvider value={props.selectedPlacementId ?? null}>
          <Ariakit.RadioGroup
            aria-labelledby={labelId}
            aria-describedby={describedBy}
            render={<PlacementList />}
          >
            {cards}
          </Ariakit.RadioGroup>
        </Ariakit.RadioProvider>
      ) : (
        <PlacementList
          role='group'
          aria-labelledby={labelId}
          aria-describedby={describedBy}
          $hideInstructions={!showInstructions}
        >
          {cards}
        </PlacementList>
      )}

      <ValidationMessage id={errorId} message={errorMessage} />
    </PlacementPickerWrapper>
  )
}
